/* Groupardy — 404 "Off the Board" page.
   Ported from the Claude Design handoff (game-board-polish, Variation C):
   a consolation game board where every clue is genuine 404-themed trivia and
   every correct response turns out to be 404. Press the BUZZER to reveal the
   answer; a hidden Daily Double tile offers a free month for feedback.
   Voice: warm, witty host. No emoji (inline SVG only). */

import React, { useState, useRef, useMemo, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import useSound from 'use-sound';
import dailyDoubleSound from 'sounds/daily-double.mp3';
import './notFound.css';

const LOGO = `${process.env.PUBLIC_URL}/g2-logo.png`;

/* ── inline icons (Material-ish stroke style) ── */
const IcHome = ({ s = 18 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v10h14V10" /><path d="M9.5 20v-6h5v6" />
  </svg>
);
const IcRefresh = ({ s = 18 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v5h-5" />
  </svg>
);
const IcMic = ({ s = 16 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" />
  </svg>
);
const IcCheck = ({ s = 18 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

function Wordmark() {
  return (
    <span className="g404-nav-wordmark" style={{
      color: '#fff',
      textShadow: '2px 2px 0 rgba(46,196,182,0.55), 4px 4px 0 rgba(255,224,102,0.45)',
    }}>Groupardy</span>
  );
}

function Nav() {
  return (
    <div className="g404-nav">
      <Link className="g404-nav-brand" to="/" aria-label="Groupardy home">
        <img src={LOGO} alt="Groupardy" /><Wordmark />
      </Link>
      <Link className="g404-nav-link" style={{ color: 'rgba(255,255,255,0.72)' }} to="/login">Sign in</Link>
    </div>
  );
}

/* ── the buzzer ── */
function Buzzer({ onBuzz, down }) {
  return (
    <button className={'g404-buzzer' + (down ? ' is-down' : '')} onClick={onBuzz} aria-label="Buzz in">
      <span className="g404-buzzer-base" />
      <span className="g404-buzzer-top"><span className="g404-buzzer-label">Buzz</span></span>
      {!down && <span className="g404-buzzer-ring" />}
    </button>
  );
}

const BOARD = [
  { cat: 'Area Codes',
    clue: <>Dial 404 and you’re ringing THIS Georgia<br />capital — the area code’s original home.</>,
    ans: 'What is Atlanta?',
    host: 'Atlanta — 404 has been ringing its phones since 1947.' },
  { cat: 'Roman Numerals',
    clue: <>Written the old-fashioned way, 404 becomes<br />this four-character Roman-numeral string.</>,
    ans: 'What is CDIV?',
    host: 'CDIV — four hundred and four. The Romans never hit a 404 page.' },
  { cat: 'Roads & Routes',
    clue: <>Ontario’s Highway 404 peels north off the 401<br />beside THIS Great Lakes metropolis.</>,
    ans: 'What is Toronto?',
    host: 'Toronto — the only 404 you can actually drive on.' },
  { cat: 'Number Play',
    clue: <>Factor 404 down and you’re left with 2, 2, and<br />THIS prime — a famously “introductory” number.</>,
    ans: 'What is 101?',
    host: '101 — so 404 is just 4 × 101. Trivia 101, if you will.' },
  { cat: 'Ancient History',
    clue: <>In 404 B.C. this city-state surrendered to<br />Sparta, ending the Peloponnesian War.</>,
    ans: 'What is Athens?',
    host: 'Athens — proof that 404 has been ruining days for ages.' },
];
const ROWS = 5;   // full board height — 1 live $404 per column, the rest spent
const DD_CAT = 'Potpourri';   // the hidden Daily Double masquerades as a category

/* measure the root so the board can reflow to a readable list on narrow screens */
function useElementWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/* reusable navy clue modal shared by the intro + every board tile */
function ClueOverlay({ category, children, revealed, onBuzz, answer, host, actions }) {
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 40,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="g404-zoom" style={{ position: 'relative', width: '100%', maxWidth: 600, borderRadius: 22,
        padding: 'clamp(24px, 3.4cqw, 34px) clamp(20px, 4cqw, 44px) clamp(26px, 3.6cqw, 36px)',
        background: 'var(--grad-final-clue)', color: '#fff', textAlign: 'center',
        boxShadow: 'inset 0 0 0 2px rgba(255,224,102,0.2), 0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ fontFamily: 'var(--font-brand)', fontSize: '0.7rem', letterSpacing: '0.24em',
          textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 16,
          textShadow: '1px 1px 0 rgba(0,0,0,0.4)' }}>{category}&nbsp;&nbsp;·&nbsp;&nbsp;$404</div>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.05rem, 4.4cqw, 1.5rem)', lineHeight: 1.2,
          textTransform: 'uppercase', margin: 0, textShadow: '2px 2px 0 rgba(0,0,0,0.4)' }}>{children}</p>

        {!revealed ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginTop: 24 }}>
            <Buzzer onBuzz={onBuzz} down={false} />
            <span className="g404-buzz-hint" style={{ color: 'rgba(255,255,255,0.6)' }}>Buzz in to reveal the answer</span>
          </div>
        ) : (
          <div className="g404-reveal">
            <div style={{ marginTop: 22, paddingTop: 20, borderTop: '2px solid rgba(255,224,102,0.33)' }}>
              <div style={{ fontFamily: 'var(--font-brand)', fontSize: '0.64rem', letterSpacing: '0.3em',
                textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 10 }}>Correct response</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem, 6.2cqw, 2.4rem)', color: 'var(--gold)',
                textShadow: '0 2px 12px rgba(255,224,102,0.3)' }}>{answer}</div>
            </div>
            <p style={{ margin: '16px auto 0', maxWidth: 'min(470px, 88cqw)', fontSize: '0.95rem', lineHeight: 1.5,
              color: 'rgba(255,255,255,0.78)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--teal)',
                fontFamily: 'var(--font-brand)', fontSize: '0.6rem', letterSpacing: '0.2em',
                textTransform: 'uppercase', marginRight: 7, verticalAlign: 'middle' }}><IcMic s={13} /> Host</span>
              {host}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 22 }}>{actions}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Daily Double easter egg — invites feedback to hi@groupardy.com for a free month */
const FEEDBACK_EMAIL = 'hi@groupardy.com';
function DailyDoubleOverlay({ onClose }) {
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.62)', zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="g404-zoom" style={{ position: 'relative', width: '100%', maxWidth: 560, borderRadius: 22,
        padding: 'clamp(26px, 3.6cqw, 38px) clamp(22px, 4cqw, 44px) clamp(26px, 3.6cqw, 36px)',
        background: 'var(--grad-final)', color: '#fff', textAlign: 'center',
        boxShadow: 'inset 0 0 0 2px rgba(255,224,102,0.28), 0 24px 70px rgba(0,0,0,0.6)' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: 22, pointerEvents: 'none',
          background: 'radial-gradient(440px 200px at 50% -12%, rgba(255,224,102,0.28), transparent 70%)' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ fontFamily: 'var(--font-brand)', fontSize: 'clamp(0.6rem, 1.6cqw, 0.72rem)',
            letterSpacing: '0.34em', textTransform: 'uppercase', color: 'var(--teal)', marginBottom: 12 }}>You found the</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.1rem, 8cqw, 3.4rem)', lineHeight: 0.96,
            color: 'var(--gold)', textTransform: 'uppercase',
            textShadow: '3px 3px 0 rgba(46,196,182,0.5), 6px 6px 0 rgba(255,224,102,0.22)' }}>Daily Double</div>
          <p style={{ margin: '16px auto 0', maxWidth: 440, fontSize: 'clamp(0.9rem, 2.4cqw, 1rem)', lineHeight: 1.55,
            color: 'rgba(255,255,255,0.82)' }}>
            Email us a line of honest feedback about Groupardy and we’ll drop a{' '}
            <strong style={{ color: 'var(--gold)' }}>free month</strong> on your account. Send it to:</p>
          <a href={`mailto:${FEEDBACK_EMAIL}?subject=Groupardy feedback`}
            style={{ display: 'inline-block', marginTop: 14, fontFamily: 'var(--font-display)',
              fontSize: 'clamp(1.3rem, 5cqw, 1.9rem)', color: 'var(--gold)', letterSpacing: '0.01em',
              textShadow: '0 2px 12px rgba(255,224,102,0.3)' }}>{FEEDBACK_EMAIL}</a>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 24 }}>
            <a className="g404-btn g404-btn-gold" href={`mailto:${FEEDBACK_EMAIL}?subject=Groupardy feedback`}>
              <IcMic s={16} /> Email your feedback</a>
            <button type="button" className="g404-btn g404-btn-ghost-dark" onClick={onClose}>
              <IcHome /> Back to the board</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  const [phase, setPhase] = useState('intro');   // 'intro' | 'board'
  const [active, setActive] = useState(null);    // trivia index, 'dd', or null
  const [revealed, setRevealed] = useState(false);
  const [used, setUsed] = useState([]);
  const [ddFound, setDdFound] = useState(false);
  const [rootRef, width] = useElementWidth();
  // the real Groupardy Daily Double sound (same asset the game board uses)
  const [playDailyDouble] = useSound(dailyDoubleSound);
  const compact = width > 0 && width < 560;   // phone / narrow → readable list
  // fixed-per-mount layout: each column's live $404 row + the hidden Daily Double cell
  const { liveRows, ddCol, ddRow, ddListPos } = useMemo(() => {
    const lr = BOARD.map(() => Math.floor(Math.random() * ROWS));
    const col = Math.floor(Math.random() * BOARD.length);
    let row = Math.floor(Math.random() * ROWS);
    if (row === lr[col]) row = (row + 1) % ROWS;
    return { liveRows: lr, ddCol: col, ddRow: row, ddListPos: Math.floor(Math.random() * (BOARD.length + 1)) };
  }, []);
  const buildRows = () => {
    const rows = BOARD.map((b, i) => ({ type: 'cat', i, b }));
    rows.splice(ddListPos, 0, { type: 'dd' });
    return rows;
  };

  const openTile = (i) => { if (used.includes(i)) return; setActive(i); setRevealed(false); };
  const closeClue = () => { setUsed((u) => u.includes(active) ? u : [...u, active]); setActive(null); setRevealed(false); };
  const openDD = () => { playDailyDouble(); setDdFound(true); setActive('dd'); };
  const closeDD = () => setActive(null);
  const allDone = used.length === BOARD.length;
  const footer = allDone
    ? 'Five clues, five answers — all 404. This board was cursed from the start. Head home?'
    : used.length === 0
      ? 'Fresh board, same curse: every clue here answers to 404. Pick a tile.'
      : `${BOARD.length - used.length} ${BOARD.length - used.length === 1 ? 'tile' : 'tiles'} left. Spotting the pattern yet?`;

  return (
    <div className="g404" ref={rootRef} style={{ background: '#0e1322' }}>
      <Helmet>
        <title>Game not found · Groupardy</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="theme-color" content="#0e1322" />
      </Helmet>
      <Nav />

      {/* board background */}
      <div style={{ position: 'absolute', inset: 0, paddingTop: 64, display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, padding: 'clamp(12px, 2cqw, 18px) clamp(12px, 2.4cqw, 22px) clamp(12px, 1.8cqw, 16px)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* category header row — wide layout only */}
          {!compact && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${BOARD.length},1fr)`, gap: 8, flex: '0 0 64px' }}>
              {BOARD.map((b, i) => (
                <div key={i} style={{ background: 'var(--teal)', borderRadius: 8, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', padding: 8, textAlign: 'center',
                  boxShadow: '0 4px 16px rgba(44,62,80,0.18)' }}>
                  <span style={{ fontFamily: 'var(--font-brand)', fontSize: '0.8rem', letterSpacing: '0.04em',
                    color: '#fff', textTransform: 'uppercase', lineHeight: 1.05,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>{b.cat}</span>
                </div>
              ))}
            </div>
          )}

          {compact ? (
            /* ── narrow: readable list of full-width category rows ── */
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 9, justifyContent: 'center' }}>
              {buildRows().map((row, idx) => {
                const isDDRow = row.type === 'dd';
                const done = isDDRow ? ddFound : used.includes(row.i);
                const label = isDDRow ? DD_CAT : row.b.cat;
                const handle = isDDRow ? openDD : () => openTile(row.i);
                return (
                  <button key={idx} className="g404-row" disabled={done} onClick={handle}
                    style={{ display: 'flex', alignItems: 'stretch', height: 'clamp(54px, 12cqw, 74px)',
                      borderRadius: 10, overflow: 'hidden', padding: 0, border: 'none', background: 'transparent',
                      boxShadow: '0 4px 16px rgba(44,62,80,0.18)' }}>
                    <div style={{ flex: 1, background: done ? 'var(--teal-deep)' : 'var(--teal)', display: 'flex',
                      alignItems: 'center', padding: '0 16px', textAlign: 'left', opacity: done ? 0.6 : 1 }}>
                      <span style={{ fontFamily: 'var(--font-brand)', fontSize: 'clamp(0.88rem, 3.8cqw, 1.05rem)',
                        letterSpacing: '0.04em', color: '#fff', textTransform: 'uppercase',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>{label}</span>
                    </div>
                    <div style={{ width: 'clamp(80px, 23cqw, 104px)', background: '#10182b', display: 'flex',
                      flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, flexShrink: 0 }}>
                      {done ? (
                        <React.Fragment>
                          <span style={{ color: 'var(--teal)', display: 'flex' }}><IcCheck s={16} /></span>
                          <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.85rem', color: 'rgba(255,224,102,0.5)' }}>404</span>
                        </React.Fragment>
                      ) : (
                        <span className="g404-row-val" style={{ fontFamily: 'var(--font-display)',
                          fontSize: 'clamp(1.1rem, 4.8cqw, 1.5rem)', color: 'var(--gold-pure)',
                          textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}>$404</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            /* ── wide: full 5×5 board — 1 live $404 per column + the hidden Daily Double ── */
            <div style={{ flex: 1, display: 'grid', gridTemplateRows: `repeat(${ROWS},1fr)`, gap: 8 }}>
              {Array.from({ length: ROWS }).map((_, r) => (
                <div key={r} style={{ display: 'grid', gridTemplateColumns: `repeat(${BOARD.length},1fr)`, gap: 8 }}>
                  {BOARD.map((b, c) => {
                    const isDD = r === ddRow && c === ddCol;
                    const isLive = liveRows[c] === r;
                    if (isDD && !ddFound) {
                      return (
                        <button key={c} className="g404-board-tile" onClick={openDD}
                          style={{ background: 'var(--navy)', borderRadius: 8, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(44,62,80,0.18)' }}>
                          <span className="g404-board-val" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(0.95rem, 3.4cqw, 1.55rem)',
                            color: 'var(--gold-pure)', textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}>$404</span>
                        </button>
                      );
                    }
                    if (isLive && !used.includes(c)) {
                      return (
                        <button key={c} className="g404-board-tile" onClick={() => openTile(c)}
                          style={{ background: 'var(--navy)', borderRadius: 8, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(44,62,80,0.18)' }}>
                          <span className="g404-board-val" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(0.95rem, 3.4cqw, 1.55rem)',
                            color: 'var(--gold-pure)', textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}>$404</span>
                        </button>
                      );
                    }
                    if (isLive && used.includes(c)) {
                      return (
                        <div key={c} style={{ background: '#0a0f1c', borderRadius: 8, display: 'flex',
                          flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                          boxShadow: 'inset 0 0 0 1px rgba(46,196,182,0.28)' }}>
                          <span style={{ color: 'var(--teal)', display: 'flex' }}><IcCheck s={15} /></span>
                          <span style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(0.62rem, 2.2cqw, 0.95rem)',
                            color: 'rgba(255,224,102,0.5)', letterSpacing: '0.04em' }}>404</span>
                        </div>
                      );
                    }
                    return (
                      <div key={c} style={{ background: '#0a0f1c', borderRadius: 8, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)' }}>
                        <span style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(0.62rem, 2.2cqw, 0.95rem)',
                          color: 'rgba(255,224,102,0.15)', letterSpacing: '0.04em' }}>404</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* host footer line */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, minHeight: 40, flexWrap: 'wrap',
            color: 'rgba(255,255,255,0.66)', fontSize: 'clamp(0.72rem, 2cqw, 0.86rem)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--teal)', flexShrink: 0,
              fontFamily: 'var(--font-brand)', fontSize: '0.58rem', letterSpacing: '0.2em',
              textTransform: 'uppercase' }}><IcMic s={12} /> Host</span>
            <span>{footer}</span>
            {allDone && (
              <Link className="g404-btn g404-btn-teal" to="/" style={{ marginLeft: 4, padding: '11px 18px', fontSize: '0.8rem' }}>
                <IcHome s={15} /> Back to home</Link>
            )}
          </div>
        </div>
      </div>

      {/* INTRO — the original page-not-found clue */}
      {phase === 'intro' && (
        <ClueOverlay category="HTTP Status Codes" revealed={revealed} onBuzz={() => setRevealed(true)}
          answer="What is 404?" host="That page isn’t on the board tonight. But the board’s got plenty more — and a funny way of always landing on 404."
          actions={<>
            <Link className="g404-btn g404-btn-teal" to="/"><IcHome /> Back to home</Link>
            <button className="g404-btn g404-btn-ghost-dark" onClick={() => { setPhase('board'); setRevealed(false); }}>
              <IcRefresh /> Pick another tile</button>
          </>}>
          This three-digit code is the<br />HTTP error a server returns<br />for “page not found.”
        </ClueOverlay>
      )}

      {/* TRIVIA — a real 404-themed clue from the board */}
      {phase === 'board' && active !== null && active !== 'dd' && (
        <ClueOverlay category={BOARD[active].cat} revealed={revealed} onBuzz={() => setRevealed(true)}
          answer={BOARD[active].ans} host={BOARD[active].host}
          actions={<>
            <button className="g404-btn g404-btn-teal" onClick={closeClue}><IcCheck /> Got it</button>
            <Link className="g404-btn g404-btn-ghost-dark" to="/"><IcHome /> Back to home</Link>
          </>}>
          {BOARD[active].clue}
        </ClueOverlay>
      )}

      {/* DAILY DOUBLE — the hidden bonus tile */}
      {phase === 'board' && active === 'dd' && <DailyDoubleOverlay onClose={closeDD} />}
    </div>
  );
}

export default NotFound;
